import { MEMBER_CODE_LENGTH, codeDigits } from "../memberCode";

/**
 * Kişi ekleme sihirbazının adımları ve kuralları (mobil
 * components/groups-manage/addPerson/addPersonSteps.ts ile aynı).
 * purpose: "member" | "manager".
 */

export const emptyAddPersonForm = () => ({
  email: "",
  // Yalnız rakamlar (gruplamasız).
  code: "",
  // Önizleme; e-posta ya da kod değişince silinir.
  person: null,
  goals: [],
  templateId: null,
  permissions: { canAddMembers: true, canRemoveMembers: true },
});

/** Üye: kişi bul, kişi, hedef ver, onay. Yönetici: kişi bul, kişi, yetkiler, onay. */
export function addPersonSteps(purpose) {
  return purpose === "manager"
    ? ["identify", "person", "permissions", "confirm"]
    : ["identify", "person", "goals", "confirm"];
}

export function looksLikeEmail(email) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(email || "").trim());
}

export function isCompleteCode(code) {
  return codeDigits(code).length === MEMBER_CODE_LENGTH;
}

/** Kişi bu amaçla zaten grupta mı (kendisi dahil). */
export function alreadyInGroup(purpose, person) {
  if (!person) return false;
  return purpose === "manager" ? Boolean(person.isManager) : Boolean(person.isMember);
}

export function canAdvance(step, purpose, form) {
  if (step === "identify") return looksLikeEmail(form.email) && isCompleteCode(form.code);
  if (step === "person") return Boolean(form.person) && !alreadyInGroup(purpose, form.person);
  return true;
}

/** E-posta ya da kod değişince önizleme geçersizdir (başka kişi olabilir). */
export function withIdentityChange(form, patch) {
  return { ...form, ...patch, person: null };
}

/**
 * Ekleme hatasından sonra gidilecek adım: 404 (kod bu arada yenilenmiş ya da
 * e-posta / kod yanlış) ilk adıma döner; diğer hatalarda adım değişmez.
 */
export function stepAfterAddError(status, current) {
  return status === 404 ? 0 : current;
}

/** Hedef adımında hedef seçilmediyse ana düğme "Şimdi değil" olur (çeviri anahtarı). */
export function goalsStepPrimaryLabel(form) {
  return form.goals.length > 0 ? "gw_next" : "ap_not_now";
}

/** Sonuç satırı: "Ahmet Yılmaz gruba eklendi · 3 hedef verildi". */
export function addResultLine({ purpose, fullName, goalCount, goalsFailed }, t) {
  if (purpose === "manager") return t("ap_result_manager", { name: fullName });
  const head = t("ap_result_member", { name: fullName });
  if (goalsFailed) return `${head} · ${t("ap_result_goals_failed")}`;
  return goalCount > 0 ? `${head} · ${t("ap_result_goals", { n: goalCount })}` : head;
}

/**
 * Ekleyen yöneticinin bu gruptaki yetkileri (grup verisinden). Alanı olmayan
 * eski kayıt ve kurucu açık sayılır (sunucuyla aynı). Grup verisi yoksa ya
 * da kişi listede değilse null: bilinmiyor, anahtarlar kısılmaz.
 */
export function actorPermissionsFrom(managers, userId) {
  if (!managers || !userId) return null;
  const me = managers.find((m) => String(m.id) === String(userId));
  if (!me) return null;
  return {
    canAddMembers: me.permissions?.canAddMembers !== false,
    canRemoveMembers: me.permissions?.canRemoveMembers !== false,
  };
}

/** Ekleyende kapalı yetki verilemez (sunucu da aynı sınırı uygular). */
export function capToActor(wanted, actor) {
  if (!actor) return wanted;
  return {
    canAddMembers: wanted.canAddMembers && actor.canAddMembers,
    canRemoveMembers: wanted.canRemoveMembers && actor.canRemoveMembers,
  };
}

/** Yönetici ekleme cevabındaki, sunucunun gerçekten verdiği yetkiler. */
export function grantedPermissionsFrom(response) {
  const granted = response?.data?.addedManager?.permissions;
  if (!granted) return null;
  return { canAddMembers: granted.canAddMembers === true, canRemoveMembers: granted.canRemoveMembers === true };
}

/** "AY" gibi baş harfler. */
export function personInitials(fullName) {
  const parts = String(fullName || "").trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "?";
  const first = parts[0][0] || "";
  const last = parts.length > 1 ? parts[parts.length - 1][0] : "";
  return `${first}${last}`.toLocaleUpperCase("tr-TR");
}
